import { makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';
import React, { useMemo } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

const useClasses = makeStyles({
    root: {
        '& p': {
            margin: `0 0 ${tokens.spacingVerticalM} 0`,
            lineHeight: 1.6,
            ':last-child': {
                marginBottom: 0,
            },
        },
        '& strong': {
            fontWeight: tokens.fontWeightSemibold,
        },
        '& em': {
            fontStyle: 'italic',
        },
        '& ul, & ol': {
            margin: `${tokens.spacingVerticalS} 0`,
            paddingLeft: tokens.spacingHorizontalXL,
        },
        '& li': {
            margin: `${tokens.spacingVerticalXXS} 0`,
            lineHeight: 1.6,
        },
        '& a': {
            color: 'inherit',
            textDecoration: 'underline',
            opacity: 0.9,
            ':hover': {
                opacity: 1,
            },
        },
        '& code': {
            backgroundColor: tokens.colorNeutralBackground3,
            ...shorthands.padding(tokens.spacingVerticalXXS, tokens.spacingHorizontalXXS),
            ...shorthands.borderRadius(tokens.borderRadiusSmall),
            fontFamily: tokens.fontFamilyMonospace,
            fontSize: '0.875em',
        },
        '& pre': {
            backgroundColor: tokens.colorNeutralBackground3,
            ...shorthands.padding(tokens.spacingVerticalM),
            ...shorthands.borderRadius(tokens.borderRadiusMedium),
            overflowX: 'auto',
            margin: `${tokens.spacingVerticalS} 0`,
            '& code': {
                backgroundColor: 'transparent',
                ...shorthands.padding(0),
            },
        },
        '& h1, & h2, & h3, & h4, & h5, & h6': {
            margin: `${tokens.spacingVerticalL} 0 ${tokens.spacingVerticalS} 0`,
            fontWeight: tokens.fontWeightSemibold,
            ':first-child': {
                marginTop: 0,
            },
        },
        '& h1': {
            fontSize: tokens.fontSizeHero800,
        },
        '& h2': {
            fontSize: tokens.fontSizeHero700,
        },
        '& h3': {
            fontSize: tokens.fontSizeBase500,
        },
        '& blockquote': {
            borderLeftWidth: '3px',
            borderLeftStyle: 'solid',
            borderLeftColor: tokens.colorNeutralStroke1,
            paddingLeft: tokens.spacingHorizontalL,
            margin: `${tokens.spacingVerticalS} 0`,
            fontStyle: 'italic',
        },
        '& hr': {
            border: 'none',
            borderTopWidth: '1px',
            borderTopStyle: 'solid',
            borderTopColor: tokens.colorNeutralStroke2,
            margin: `${tokens.spacingVerticalL} 0`,
        },
    },
    tableWrapper: {
        overflowX: 'auto',
        margin: `${tokens.spacingVerticalM} 0`,
        maxWidth: '100%',
    },
    table: {
        // Use intrinsic sizing, but keep the table bounded by the container.
        tableLayout: 'auto',
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: tokens.fontSizeBase300,
        lineHeight: 1.5,
        // Constrain column widths so a very long first column doesn't force the
        // other columns to stretch. Cells will wrap within their column.
        '& th:nth-child(1), & td:nth-child(1)': {
            maxWidth: '180ch',
            whiteSpace: 'normal',
            overflowWrap: 'break-word',
        },
        '& th:nth-child(2), & td:nth-child(2)': {
            minWidth: '10ch',
            whiteSpace: 'normal',
            overflowWrap: 'break-word',
        },
        '& th:nth-child(3), & td:nth-child(3)': {
            minWidth: '15ch',
            whiteSpace: 'normal',
            overflowWrap: 'break-word',
        },
    },
    tableHeaderCell: {
        backgroundColor: tokens.colorNeutralBackground3,
        fontWeight: tokens.fontWeightSemibold,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalS),
        textAlign: 'left',
        verticalAlign: 'top',
        whiteSpace: 'normal',
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
    },
    tableCell: {
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalS),
        textAlign: 'left',
        verticalAlign: 'top',
        whiteSpace: 'normal',
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
    },
    tableRow: {
        ':nth-child(even)': {
            backgroundColor: tokens.colorNeutralBackground2,
        },
    },
});

/** Palette for citation colour-coding (must match ChatMessages.CITATION_COLORS) */
const CITATION_COLORS = [
    '#2563eb',
    '#059669',
    '#b45309',
    '#dc2626',
    '#7c3aed',
    '#db2777',
    '#0891b2',
    '#65a30d',
];

/** Converts CITATION-ref-messageId placeholders to markdown links for react-markdown. */
const preprocessCitationPlaceholders = (content: string, messageId: string): string => {
    if (!messageId) return content;
    return content.replace(/CITATION-(\d+)-([a-f0-9-]+)/g, (_, ref) => `[${ref}](#citation-${ref})`);
};

interface MarkdownRendererProps {
    content: string;
    className?: string;
    /** When set, content may contain CITATION-ref-messageId placeholders; they are turned into citation links. */
    citationMessageId?: string;
    /** Returns hex colour for a citation ref (for colour-coding). */
    getCitationColor?: (ref: string) => string;
}

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
    content,
    className,
    citationMessageId,
    getCitationColor = (ref) => CITATION_COLORS[parseInt(ref, 10) % CITATION_COLORS.length] ?? CITATION_COLORS[0],
}) => {
    const classes = useClasses();

    const markdown = useMemo(
        () => preprocessCitationPlaceholders(content, citationMessageId ?? ''),
        [content, citationMessageId],
    );

    const components = useMemo<Components>(
        () => ({
            table: ({ children }) => (
                <div className={classes.tableWrapper}>
                    <table className={classes.table}>{children}</table>
                </div>
            ),
            th: ({ children }) => <th className={classes.tableHeaderCell}>{children}</th>,
            td: ({ children }) => <td className={classes.tableCell}>{children}</td>,
            tr: ({ children }) => <tr className={classes.tableRow}>{children}</tr>,
            a: ({ href, children, ...props }) => {
                if (href?.startsWith('#citation-')) {
                    const ref = href.slice('#citation-'.length);
                    const color = getCitationColor(ref);
                    return (
                        <a
                            href="#"
                            className="citation-ref"
                            data-citation-ref={ref}
                            role="button"
                            style={{
                                color,
                                fontWeight: 600,
                                textDecoration: 'underline',
                            }}
                            onClick={(e) => {
                                e.preventDefault();
                            }}
                            {...props}
                        >
                            {children}
                        </a>
                    );
                }
                return (
                    <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                        {children}
                    </a>
                );
            },
        }),
        [classes, getCitationColor],
    );

    if (!content) {
        return null;
    }

    return (
        <div className={mergeClasses(classes.root, className)}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
                {markdown}
            </ReactMarkdown>
        </div>
    );
};
